import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  BriefcaseBusiness,
  CalendarDays,
  ChevronRight,
  CreditCard,
  FileText,
  FolderKanban,
  LayoutDashboard,
  LogOut,
  Menu,
  Settings,
  UserCog,
  Wrench,
  X,
} from 'lucide-react';

const navSections = [
  {
    label: 'Principal',
    items: [
      {
        to: '/admin/dashboard',
        icon: LayoutDashboard,
        label: 'Tableau de bord',
      },
      {
        to: '/admin/pipeline',
        icon: FolderKanban,
        label: 'Pipeline CRM',
        badge: '7',
      },
      {
        to: '/admin/devis',
        icon: FileText,
        label: 'Devis',
      },
    ],
  },
  {
    label: 'Opérations',
    items: [
      {
        to: '/admin/planning',
        icon: CalendarDays,
        label: 'Planning',
      },
      {
        to: '/admin/techniciens',
        icon: UserCog,
        label: 'Techniciens',
      },
      {
        to: '/admin/interventions',
        icon: Wrench,
        label: 'Interventions',
        badge: '3',
      },
    ],
  },
  {
    label: 'Finance',
    items: [
      {
        to: '/admin/facturation',
        icon: BriefcaseBusiness,
        label: 'Facturation',
      },
      {
        to: '/admin/paiements',
        icon: CreditCard,
        label: 'Paiements',
      },
    ],
  },
  {
    label: 'Pilotage site',
    items: [
      {
        to: '/admin/contacts',
        icon: Bell,
        label: 'Messages',
      },
      {
        to: '/admin/prestations',
        icon: ChevronRight,
        label: 'Prestations',
      },
      {
        to: '/admin/cms',
        icon: ChevronRight,
        label: 'CMS',
      },
      {
        to: '/admin/seo',
        icon: ChevronRight,
        label: 'SEO',
      },
      {
        to: '/admin/settings',
        icon: Settings,
        label: 'Paramètres',
      },
    ],
  },
];

const routeMeta = {
  '/admin/dashboard': {
    title: 'Tableau de bord',
    actionLabel: '+ Nouveau lead',
  },
  '/admin/pipeline': {
    title: 'Pipeline CRM',
    actionLabel: '+ Ajouter une opportunité',
  },
  '/admin/devis': {
    title: 'Devis',
    actionLabel: '+ Créer un devis',
  },
  '/admin/planning': {
    title: 'Planning',
    actionLabel: '+ Planifier une visite',
  },
  '/admin/techniciens': {
    title: 'Techniciens',
    actionLabel: '+ Ajouter un technicien',
  },
  '/admin/interventions': {
    title: 'Interventions',
    actionLabel: '+ Nouvelle intervention',
  },
  '/admin/facturation': {
    title: 'Facturation',
    actionLabel: '+ Générer une facture',
  },
  '/admin/paiements': {
    title: 'Paiements',
    actionLabel: '+ Enregistrer un paiement',
  },
  '/admin/contacts': {
    title: 'Messages',
  },
  '/admin/prestations': {
    title: 'Prestations',
  },
  '/admin/cms': {
    title: 'CMS',
  },
  '/admin/seo': {
    title: 'SEO',
  },
  '/admin/settings': {
    title: 'Paramètres',
  },
};

const resolveRouteMeta = (pathname) => {
  if (pathname.startsWith('/admin/devis/')) {
    return { title: 'Détail du devis' };
  }
  return routeMeta[pathname] || { title: 'Administration IDMAT Service' };
};

const AdminLayout = ({
  children,
  title,
  actionLabel,
  onAction,
  actionTo,
  contentClassName = '',
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const isAdmin = localStorage.getItem('idmat_admin');
    if (!isAdmin) {
      navigate('/admin');
    }
  }, [navigate]);

  useEffect(() => {
    document.body.classList.add('crm-admin-theme');
    return () => {
      document.body.classList.remove('crm-admin-theme');
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('idmat_admin');
    navigate('/admin');
  };

  const currentMeta = resolveRouteMeta(location.pathname);
  const pageTitle = title || currentMeta.title;
  const primaryActionLabel = actionLabel || currentMeta.actionLabel;

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const renderPrimaryAction = () => {
    if (!primaryActionLabel || (!actionTo && !onAction)) return null;

    if (actionTo) {
      return (
        <Link to={actionTo} className="crm-btn-primary">
          {primaryActionLabel}
        </Link>
      );
    }

    return (
      <button type="button" className="crm-btn-primary" onClick={onAction}>
        {primaryActionLabel}
      </button>
    );
  };

  return (
    <div className="crm-admin-shell" data-testid="admin-layout">
      <aside className={`crm-sidebar ${sidebarOpen ? 'is-open' : ''}`}>
        <div className="crm-sidebar__brand">
          <Link to="/admin/dashboard" className="crm-sidebar__brand-link">
            <span className="crm-sidebar__tag">IDMAT</span>
            <span className="crm-sidebar__name">Service</span>
          </Link>
        </div>

        <nav className="crm-sidebar__nav">
          {navSections.map((section) => (
            <div key={section.label}>
              <div className="crm-sidebar__section">{section.label}</div>
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setSidebarOpen(false)}
                    className={`crm-nav-item ${isActive(item.to) ? 'is-active' : ''}`}
                  >
                    <Icon size={16} className="crm-nav-item__icon" />
                    <span>{item.label}</span>
                    {item.badge && (
                      <span className="crm-nav-item__badge">{item.badge}</span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="crm-sidebar__footer">
          <div className="crm-user-chip">
            <div className="crm-user-chip__avatar">RH</div>
            <div>
              <div className="crm-user-chip__name">Rudolph H.</div>
              <div className="crm-user-chip__role">Admin</div>
            </div>
          </div>
          <button type="button" className="crm-logout" onClick={handleLogout}>
            <LogOut size={15} />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <button
          type="button"
          className="crm-sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-label="Fermer le menu"
        />
      )}

      <div className="crm-main">
        <header className="crm-topbar">
          <button
            type="button"
            className="crm-icon-btn crm-mobile-only"
            onClick={() => setSidebarOpen(true)}
            aria-label="Ouvrir le menu"
          >
            <Menu size={18} />
          </button>

          <div className="crm-topbar__title">{pageTitle}</div>
          <button type="button" className="crm-date-pill">
            Aujourd&apos;hui
          </button>
          <div className="crm-spacer" />
          <Link to="/" target="_blank" className="crm-link-secondary">
            Voir le site
          </Link>
          {renderPrimaryAction()}
          <button
            type="button"
            className="crm-icon-btn crm-mobile-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fermer le menu"
          >
            <X size={18} />
          </button>
        </header>

        <main className={`crm-content ${contentClassName}`.trim()}>{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
